import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ShoppingBag, Sparkles, ExternalLink, Tag, ShieldCheck, Package, ArrowRight, Heart, MapPin, MessageCircle } from "lucide-react";
import StoreBuyNowModal from "@/components/StoreBuyNowModal";

const CATS = [
  { key: "all", label: "All" },
  { key: "corporate", label: "Corporate" },
  { key: "genz", label: "Gen Z" },
  { key: "girls", label: "Girls" },
  { key: "college", label: "College Gifting" },
  { key: "concert", label: "Event Drops" },
  { key: "tech", label: "Accessories & Tech" },
  { key: "lifestyle", label: "Lifestyle" },
  { key: "collectible", label: "Collectibles" },
  { key: "affiliate", label: "Curated Picks" },
];

function ProductCard({ p, onBuy }) {
  const isAff = p.category === "affiliate" && p.affiliate_url;
  const outbound = () => {
    api.post(`/store/products/${p.id}/click`).catch(() => {});
    if (p.affiliate_url) window.open(p.affiliate_url, "_blank", "noopener");
  };
  return (
    <div className="rounded-3xl overflow-hidden border border-white/10 bg-white/[0.04] hover:border-lime/50 transition-colors group" data-testid={`store-p-${p.id}`}>
      <div className="relative aspect-[4/5] overflow-hidden">
        <img src={p.image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {isAff ? (
            <span className="chip !text-orangex !border-orangex/40 !bg-orangex/10 !text-[10px]">{p.partner || "Partner"} · Affiliate</span>
          ) : (
            <span className="chip !text-lime !border-lime/40 !bg-lime/10 !text-[10px]"><Sparkles className="w-3 h-3" /> Eventom Original</span>
          )}
          {p.featured && <span className="chip !text-magenta !border-magenta/40 !bg-magenta/10 !text-[10px]">Featured</span>}
        </div>
        {p.strike_price > p.price && (
          <span className="absolute top-3 right-3 px-2 py-1 rounded-full bg-black/60 backdrop-blur text-lime text-[10px] font-mono uppercase tracking-widest font-bold">
            {Math.round(((p.strike_price - p.price) / p.strike_price) * 100)}% OFF
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="font-heading font-bold text-white text-sm leading-tight line-clamp-2">{p.title}</div>
        {p.description && <div className="text-[11px] text-muted mt-1.5 line-clamp-2">{p.description}</div>}
        <div className="mt-3 flex items-end justify-between gap-2">
          <div>
            <div className="text-lg font-fancy text-lime leading-none">₹{Number(p.price).toLocaleString("en-IN")}</div>
            {p.strike_price > p.price && <div className="text-[10px] text-muted line-through">₹{Number(p.strike_price).toLocaleString("en-IN")}</div>}
          </div>
          {isAff ? (
            <button onClick={outbound} data-testid={`store-buy-${p.id}`} className="px-3 py-2 rounded-full text-[10px] font-heading font-bold uppercase tracking-widest bg-orangex text-ink hover:bg-orangex/90 inline-flex items-center gap-1">
              Shop <ExternalLink className="w-3 h-3" />
            </button>
          ) : (
            <button onClick={() => onBuy(p)} data-testid={`store-buy-${p.id}`} className="px-3 py-2 rounded-full text-[10px] font-heading font-bold uppercase tracking-widest bg-lime text-ink hover:bg-lime/90 inline-flex items-center gap-1">
              Buy <ShoppingBag className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const RAIL_ART = {
  organiser: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1200&auto=format&fit=crop&q=70",
  artist:    "/assets/girl-street.webp",
  you:       "/assets/festival-girls.webp",
};
const RAIL_ACCENT = { organiser: "lime", artist: "skyx", you: "magenta" };

function AudienceRails({ rails, onBuy }) {
  if (!rails || rails.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="shop-audience-rails">
      {rails.map((r) => {
        const accent = RAIL_ACCENT[r.key] || "lime";
        const isAff = (p) => p.category === "affiliate" && p.affiliate_url;
        return (
          <div key={r.key} className="rounded-3xl overflow-hidden border border-white/10 bg-white/[0.03]" data-testid={`shop-rail-${r.key}`}>
            <div className="grid md:grid-cols-5 min-h-[220px]">
              <div className="md:col-span-2 relative">
                <img src={RAIL_ART[r.key] || RAIL_ART.you} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/60 to-transparent" />
                <div className="relative p-6 sm:p-8 h-full flex flex-col justify-end text-white">
                  <div className={`text-[10px] font-mono uppercase tracking-widest text-${accent}`}>{r.audience}</div>
                  <h3 className="font-fancy text-3xl md:text-4xl leading-[0.95] mt-1">{r.pitch}</h3>
                  {r.wa_prefill && (
                    <a
                      href={`https://wa.me/917250120666?text=${encodeURIComponent(r.wa_prefill)}`}
                      target="_blank" rel="noreferrer"
                      data-testid={`shop-rail-cta-${r.key}`}
                      className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-full bg-white text-ink text-[11px] font-heading font-bold uppercase tracking-widest w-max hover:bg-lime transition-colors"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> {r.cta}
                    </a>
                  )}
                </div>
              </div>
              <div className="md:col-span-3 p-4 sm:p-5">
                {r.products.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-muted font-mono min-h-[180px]">Curating drops now.</div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {r.products.slice(0, 4).map((p) => (
                      <div key={p.id} className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03]" data-testid={`shop-rail-${r.key}-p-${p.id}`}>
                        <div className="aspect-square overflow-hidden">
                          <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="p-2.5">
                          <div className="text-[10px] font-heading font-bold text-white line-clamp-2 leading-tight">{p.title}</div>
                          <div className="flex items-center justify-between mt-2">
                            <span className={`text-[11px] font-mono text-${accent}`}>₹{Number(p.price).toLocaleString("en-IN")}</span>
                            <button
                              onClick={() => isAff(p) ? window.open(p.affiliate_url, "_blank", "noopener") : onBuy(p)}
                              data-testid={`shop-rail-${r.key}-buy-${p.id}`}
                              className={`px-2 py-1 rounded-full text-[9px] font-heading font-bold uppercase tracking-widest bg-${accent} text-ink hover:brightness-110`}
                            >
                              {isAff(p) ? "Shop" : "Buy"}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}

export default function Store() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [rails, setRails] = useState([]);
  useEffect(() => {
    api.get("/store/products").then(({ data }) => setItems(data || [])).catch(() => {}).finally(() => setLoading(false));
    api.get("/shop/audience-rails").then(({ data }) => setRails(data?.rails || [])).catch(() => {});
  }, []);
  const filtered = useMemo(() => (cat === "all" ? items : items.filter((x) => x.collection === cat || (cat === "affiliate" && x.category === "affiliate"))), [items, cat]);
  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="store-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 lg:py-14 grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-7 relative rounded-3xl overflow-hidden border border-white/10 bg-[#0F0F16]">
            <div className="absolute inset-0 opacity-45">
              <img src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1600" alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-transparent" />
            </div>
            <div className="relative p-8 lg:p-12">
              <div className="chip !text-lime !border-lime/40 !bg-lime/10"><Sparkles className="w-3 h-3" /> Eventom Store</div>
              <h1 className="mt-4 font-fancy text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]" data-testid="store-headline">Wear the vibe.<br /><span className="marker-lime">Live the night.</span></h1>
              <p className="mt-4 text-white/70 text-lg max-w-lg">Exclusive Eventom drops and curated event-ready finds. Every hoodie, cap and sticker is a piece of Bharat's live culture.</p>
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-[11px] font-mono uppercase tracking-widest text-white/60">
                <span className="inline-flex items-center gap-1.5"><Package className="w-3.5 h-3.5 text-lime" /> Ships pan-India</span>
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-magenta" /> COD available</span>
                <span className="inline-flex items-center gap-1.5"><Tag className="w-3.5 h-3.5 text-orangex" /> Members save more</span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3" data-testid="store-hero-tiles">
            {items.filter((x) => x.featured).slice(0, 4).map((p) => (
              <div key={p.id} className="rounded-3xl overflow-hidden border border-white/10 bg-white/[0.04] p-3">
                <img src={p.image_url} alt={p.title} className="w-full h-32 object-cover rounded-2xl" />
                <div className="text-[11px] font-heading font-bold text-white mt-2 line-clamp-2 leading-tight">{p.title}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-lime mt-1">₹{Number(p.price).toLocaleString("en-IN")}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THEMED COLOR STRIP — PDF magazine signature between hero & shop */}
      <section className="themed-blue sticker-wall py-6 overflow-hidden" data-testid="store-themed-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/85">● Members only</div>
            <div className="font-fancy uppercase text-white leading-none mt-1" style={{ fontSize: "clamp(24px, 4vw, 44px)", textShadow: "2px 2px 0 rgba(0,0,0,0.3)" }}>
              Shop the <span className="text-striped">Night.</span>
            </div>
          </div>
          <Link to="/clubvip" className="btn-pill-white !text-[11px]" data-testid="store-vip-strip-cta">Unlock 20% off →</Link>
        </div>
      </section>

      {/* AUDIENCE RAILS — 3 doors: Organisers · Artists · You */}
      <AudienceRails rails={rails} onBuy={setBuying} />

      {/* CATEGORY CHIPS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 space-y-3" data-testid="store-cats">
        {/* Live coupon strip */}
        <div className="rounded-2xl border border-lime/40 bg-lime/10 px-4 py-2 flex flex-wrap items-center justify-between gap-2" data-testid="store-coupon-strip">
          <div className="text-[11px] text-white/90 inline-flex items-center gap-2">
            <Tag className="w-3.5 h-3.5 text-lime" />
            <span>Use code <span className="font-mono font-bold text-lime tracking-wider">LAUNCH50</span> at checkout — <span className="text-lime">50% off</span> your first Eventom Original.</span>
          </div>
          <button
            onClick={() => { navigator.clipboard.writeText("LAUNCH50"); import("sonner").then(({ toast }) => toast.success("LAUNCH50 copied")); }}
            data-testid="store-coupon-copy"
            className="text-[10px] font-mono uppercase tracking-widest text-lime underline">Copy code</button>
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
          {CATS.map(({ key, label }) => (
            <button key={key} onClick={() => setCat(key)} data-testid={`store-cat-${key}`}
              className={`shrink-0 px-4 py-2 rounded-full text-[11px] font-heading font-bold uppercase tracking-widest border transition-all ${cat === key ? "border-lime bg-lime/15 text-lime" : "border-white/15 bg-white/5 text-white/70 hover:text-white"}`}>{label}</button>
          ))}
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1,2,3,4,5,6,7,8].map((i) => <div key={i} className="rounded-3xl bg-white/5 border border-white/10 aspect-[4/5] animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/15 bg-white/5 p-12 text-center" data-testid="store-empty">
            <div className="font-fancy text-2xl text-white">No products here yet.</div>
            <p className="text-sm text-muted mt-2">Come back soon — new drops every week.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="store-grid">
            {filtered.map((p) => <ProductCard key={p.id} p={p} onBuy={setBuying} />)}
          </div>
        )}
      </section>

      {/* MADE IN BHARAT — culture strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-8" data-testid="store-bharat">
        <div className="rounded-3xl overflow-hidden relative border border-white/10 grid md:grid-cols-5 min-h-[380px]">
          <div className="md:col-span-3 relative">
            <img src="https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=1600&auto=format&fit=crop&q=60" alt="Made in Bharat" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
            <div className="relative p-8 sm:p-10 text-white h-full flex flex-col justify-end">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-lime text-ink text-[10px] font-mono font-bold uppercase tracking-widest w-max"><MapPin className="w-3 h-3" /> Made in Bharat</div>
              <h2 className="mt-3 font-fancy text-4xl sm:text-5xl md:text-6xl leading-[0.95]">Every hoodie is a<br/><span className="marker-lime">piece of culture.</span></h2>
              <p className="mt-3 max-w-lg text-white/85">Screen-printed in Ludhiana. Embroidered in Tiruppur. Photographed in Patna, Kolkata & Bengaluru. Shipped by teams building India's live culture — one drop at a time.</p>
            </div>
          </div>
          <div className="md:col-span-2 bg-[#0F0F16] p-6 sm:p-8 flex flex-col justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-lime">Why it matters</div>
              <ul className="mt-3 space-y-3 text-sm text-white/85">
                <li className="flex gap-3"><Heart className="w-5 h-5 text-magenta shrink-0" /><span><span className="font-heading font-bold text-white block">100% Indian craftsmanship.</span>Cotton mills in Tamil Nadu, printing units in Punjab, packaging in Bihar.</span></li>
                <li className="flex gap-3"><Package className="w-5 h-5 text-lime shrink-0" /><span><span className="font-heading font-bold text-white block">Ship pan-India in 3–5 days.</span>Free shipping above ₹999. Cash on delivery available across 15,000+ pincodes.</span></li>
                <li className="flex gap-3"><ShieldCheck className="w-5 h-5 text-skyx shrink-0" /><span><span className="font-heading font-bold text-white block">Fair trade, fair prices.</span>No middlemen. Every ₹ helps us fund more free tools for organisers.</span></li>
              </ul>
            </div>
            <div className="mt-4 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-white/50">
              <span>🇮🇳 Bharat 1st</span>
              <span>·</span>
              <span>Since 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* AFFILIATE NOTE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="rounded-3xl border border-white/10 bg-[#0F0F16] p-6 flex flex-wrap items-center justify-between gap-4" data-testid="store-affiliate-note">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-orangex">Curated finds · affiliate</div>
            <div className="font-heading font-bold text-white mt-1">Some links earn Eventom a small commission — at no cost to you.</div>
            <div className="text-xs text-muted mt-1">Every ₹ we earn helps us build more free tools for organisers and fans.</div>
          </div>
          <a href="/organisers" className="btn-ghost !py-2 !px-4 !text-xs">List your product <ArrowRight className="w-3 h-3" /></a>
        </div>
      </section>

      {buying && <StoreBuyNowModal product={buying} onClose={() => setBuying(null)} onSuccess={() => setBuying(null)} />}
    </Layout>
  );
}
